import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Search, MapPin, Plus, X, ExternalLink, AlertCircle, Truck } from 'lucide-react';

const ACTIVE_STATUSES = ['CONFIRMED', 'DEPARTED', 'IN_TRANSIT'];
const ALL_STATUSES = ['PENDING', 'CONFIRMED', 'DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'RETURNED', 'CANCELLED'];

// ── Journey stages ──────────────────────────────────────────────────
const STAGES = [
  { key: 'PENDING',    label: 'Pending',    pct: 0   },
  { key: 'CONFIRMED',  label: 'Confirmed',  pct: 20  },
  { key: 'DEPARTED',   label: 'Departed',   pct: 45  },
  { key: 'IN_TRANSIT', label: 'In Transit', pct: 70  },
  { key: 'DELIVERED',  label: 'Delivered',  pct: 100 },
];

function stagePct(status: string): number {
  const s = STAGES.find(s => s.key === status);
  return s ? s.pct : (status === 'RETURNED' || status === 'CANCELLED' ? 0 : 0);
}

const isMoving = (status: string) => ['DEPARTED', 'IN_TRANSIT'].includes(status);

function JourneyPanel({ shipment, latestLocation }: { shipment: any; latestLocation: string }) {
  const pct = stagePct(shipment.status);
  const moving = isMoving(shipment.status);
  const delivered = shipment.status === 'DELIVERED';
  const cancelled = ['CANCELLED', 'RETURNED'].includes(shipment.status);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ fontWeight: 600, fontSize: 15, color: '#64748b' }}>Journey</h3>
        {moving && (
          <span style={{ fontSize: 11, background: '#dcfce7', color: '#15803d', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>
            ● Live
          </span>
        )}
        {delivered && (
          <span style={{ fontSize: 11, background: '#dbeafe', color: '#1d4ed8', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>
            ✓ Delivered
          </span>
        )}
        {cancelled && (
          <span style={{ fontSize: 11, background: '#fee2e2', color: '#b91c1c', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>
            {shipment.status}
          </span>
        )}
      </div>

      {/* Origin → Destination labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748b' }}>
          <Truck size={13} style={{ color: '#3b82f6' }} />
          <span>{shipment.transporter ? `${shipment.transporter.firstName} ${shipment.transporter.lastName}` : 'No Driver'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748b' }}>
          <span>{shipment.receiverCity || shipment.receiverAddress || 'Destination'}</span>
          <MapPin size={13} style={{ color: '#22c55e' }} />
        </div>
      </div>

      {/* Road track */}
      <div style={{ position: 'relative', height: 56, marginBottom: 8 }}>
        {/* Road background */}
        <div style={{
          position: 'absolute', top: '50%', left: 0, right: 0,
          height: 14, transform: 'translateY(-50%)',
          background: '#e2e8f0', borderRadius: 8, overflow: 'hidden',
        }}>
          {/* Filled progress */}
          <div style={{
            position: 'absolute', top: 0, left: 0, bottom: 0,
            width: `${pct}%`,
            background: cancelled ? '#fca5a5' : 'linear-gradient(90deg, #3b82f6, #06b6d4)',
            borderRadius: 8,
            transition: 'width 0.8s ease',
          }} />
          {/* Moving dashes on the unfilled road */}
          {moving && (
            <div className="journey-road-moving" style={{
              position: 'absolute', top: '45%', left: `${pct}%`, right: 0, height: 2,
              backgroundImage: 'repeating-linear-gradient(90deg, #94a3b8 0px, #94a3b8 10px, transparent 10px, transparent 20px)',
              backgroundSize: '40px 2px',
            }} />
          )}
        </div>

        {/* Stage dots */}
        {STAGES.map(stage => {
          const reached = stagePct(shipment.status) >= stage.pct;
          const isCurrent = shipment.status === stage.key;
          return (
            <div key={stage.key} style={{
              position: 'absolute',
              left: `${stage.pct}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
            }}>
              <div style={{
                width: isCurrent ? 16 : 12,
                height: isCurrent ? 16 : 12,
                borderRadius: '50%',
                background: reached ? (cancelled ? '#ef4444' : '#3b82f6') : '#cbd5e1',
                border: `2px solid ${reached ? (cancelled ? '#fca5a5' : '#bfdbfe') : '#e2e8f0'}`,
                transition: 'all 0.4s ease',
                boxShadow: isCurrent ? '0 0 0 3px rgba(59,130,246,0.2)' : 'none',
              }} />
            </div>
          );
        })}

        {/* Car */}
        {!cancelled && (
          <div style={{
            position: 'absolute',
            left: `${pct}%`,
            top: '2%',
            transform: 'translateX(-50%)',
            zIndex: 3,
            transition: 'left 0.8s ease',
          }}>
            {/* Speed lines (left of car when moving) */}
            {moving && (
              <div style={{
                position: 'absolute', right: '100%', top: '30%',
                display: 'flex', flexDirection: 'column', gap: 2, paddingRight: 3,
              }}>
                <div className="journey-speed-line" style={{ width: 10, height: 2, background: 'rgba(59,130,246,0.6)', borderRadius: 1 }} />
                <div className="journey-speed-line journey-speed-line-2" style={{ width: 14, height: 2, background: 'rgba(6,182,212,0.5)', borderRadius: 1 }} />
                <div className="journey-speed-line journey-speed-line-3" style={{ width: 8, height: 2, background: 'rgba(59,130,246,0.4)', borderRadius: 1 }} />
              </div>
            )}

            {/* Car body */}
            <div
              className={moving ? 'journey-truck' : ''}
              style={{
                background: delivered ? '#22c55e' : '#3b82f6',
                borderRadius: 6,
                padding: '4px 7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: moving
                  ? '0 4px 14px rgba(59,130,246,0.5)'
                  : delivered
                    ? '0 2px 8px rgba(34,197,94,0.3)'
                    : '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              <Truck size={15} color="#fff" />
            </div>

            {/* Wheels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingInline: 4, marginTop: 2 }}>
              <div className={moving ? 'journey-wheel' : ''} style={{
                width: 7, height: 7, borderRadius: '50%',
                background: '#1e293b', border: '2px solid #64748b',
              }} />
              <div className={moving ? 'journey-wheel' : ''} style={{
                width: 7, height: 7, borderRadius: '50%',
                background: '#1e293b', border: '2px solid #64748b',
              }} />
            </div>
          </div>
        )}
      </div>

      {/* Stage labels */}
      <div style={{ position: 'relative', height: 28 }}>
        {STAGES.map(stage => (
          <div key={stage.key} style={{
            position: 'absolute',
            left: `${stage.pct}%`,
            transform: 'translateX(-50%)',
            fontSize: 10,
            fontWeight: shipment.status === stage.key ? 700 : 400,
            color: stagePct(shipment.status) >= stage.pct ? '#1e293b' : '#94a3b8',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}>
            {stage.label}
          </div>
        ))}
      </div>

      {/* Current location info */}
      {(latestLocation || shipment.departureTime) && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 20, fontSize: 12, color: '#64748b' }}>
          {latestLocation && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <MapPin size={12} style={{ color: '#3b82f6' }} />
              <span><strong>Last seen:</strong> {latestLocation}</span>
            </div>
          )}
          {shipment.departureTime && (
            <div>
              <strong>Departed:</strong> {new Date(shipment.departureTime).toLocaleString()}
            </div>
          )}
          {shipment.estimatedDeliveryTime && shipment.status !== 'DELIVERED' && (
            <div>
              <strong>ETA:</strong> {new Date(shipment.estimatedDeliveryTime).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const statusColor = (s: string) => s.toLowerCase().replace(/_/g, '-');

export const Tracking: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Company shipment list (left panel) ──
  const [shipments, setShipments] = useState<any[]>([]);
  const [loadingShipments, setLoadingShipments] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  // ── Tracking number search ──
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // ── Selected shipment + history ──
  const [selected, setSelected] = useState<any | null>(null);
  const [isOwnShipment, setIsOwnShipment] = useState(false);
  const [trackingHistory, setTrackingHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── Add tracking update ──
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [updateForm, setUpdateForm] = useState({ location: '', description: '', status: '' });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState('');

  const shipmentIsMoving = ['DEPARTED', 'IN_TRANSIT'].includes(selected?.status ?? '');
  const canAddUpdate = isOwnShipment && user && (
    shipmentIsMoving
      ? ['SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'COMPANY_AGENT', 'TRANSPORTER'].includes(user.role)
      : ['SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'COMPANY_AGENT'].includes(user.role)
  );

  // ── Load company shipments ──
  const loadShipments = useCallback(async () => {
    setLoadingShipments(true);
    try {
      const companyId = user?.role !== 'SUPER_ADMIN' ? user?.companyId : undefined;
      const status = statusFilter || undefined;
      const res = await apiClient.listShipments({ companyId, status }, 200, 0);
      const data = res.data.data;
      let list: any[] = data?.shipments ?? data?.rows ?? (Array.isArray(data) ? data : []);
      // For company users, default view shows only active (moving) shipments.
      // For INDEPENDENT_USER, show all their shipments so PENDING is visible too.
      if (!statusFilter && user?.role !== 'INDEPENDENT_USER') {
        list = list.filter((s: any) => ACTIVE_STATUSES.includes(s.status));
      }
      setShipments(list);
    } catch {/* silent */}
    finally { setLoadingShipments(false); }
  }, [user, statusFilter]);

  useEffect(() => { loadShipments(); }, [loadShipments]);

  // ── Select a shipment from the list (always own company) ──
  const selectFromList = async (shipment: any) => {
    setIsOwnShipment(true);
    setShowAddUpdate(false);
    setUpdateForm({ location: '', description: '', status: '' });
    setUpdateError('');
    setSearchInput('');
    setSearchError('');
    // Fetch full detail so transporter and all associations are included
    try {
      const res = await apiClient.getShipment(shipment.id);
      setSelected(res.data.data);
    } catch {
      setSelected(shipment);
    }
    await fetchHistory(shipment.id);
  };

  // ── Search by tracking number (uses public endpoint — works for any shipment) ──
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const tn = searchInput.trim();
    if (!tn) return;
    setSearching(true);
    setSearchError('');
    setSelected(null);
    try {
      const res = await apiClient.publicTracking(tn);
      const shipment = res.data.data;

      // Check if this shipment belongs to the user's company or was sent by this user
      const own = user?.role === 'SUPER_ADMIN' ||
                  shipment.companyId === user?.companyId ||
                  shipment.senderId === user?.id;
      setIsOwnShipment(own);
      setSelected(shipment);
      setTrackingHistory(shipment.tracking ?? []);
      setShowAddUpdate(false);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setSearchError(`No shipment found with tracking number "${tn}"`);
      } else {
        setSearchError('Search failed. Please try again.');
      }
    } finally { setSearching(false); }
  };

  const fetchHistory = async (id: string) => {
    setLoadingHistory(true);
    try {
      const res = await apiClient.getTrackingHistory(id);
      setTrackingHistory(res.data.data ?? []);
    } catch { setTrackingHistory([]); }
    finally { setLoadingHistory(false); }
  };

  // ── Add tracking update ──
  const handleAddUpdate = async (e: React.FormEvent) => {
    e.preventDefault(); setUpdateError(''); setUpdateLoading(true);
    try {
      await apiClient.updateTracking(selected.id, {
        location: updateForm.location || undefined,
        description: updateForm.description || undefined,
        status: updateForm.status || undefined,
      });
      setShowAddUpdate(false);
      setUpdateForm({ location: '', description: '', status: '' });
      await fetchHistory(selected.id);
      // Re-fetch full shipment so truck moves to the new status position
      try {
        const fresh = await apiClient.getShipment(selected.id);
        setSelected(fresh.data.data);
      } catch {}
      loadShipments();
    } catch (err: any) {
      setUpdateError(err.response?.data?.error || 'Failed to add update');
    } finally { setUpdateLoading(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, alignItems: 'start' }}>

      {/* ── Left panel ── */}
      <div>
        <div className="page-header" style={{ marginBottom: 12 }}>
          <h1 className="page-title">Monitor Tracking</h1>
        </div>

        {/* Tracking number search */}
        <form onSubmit={handleSearch} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                placeholder="Enter tracking number..."
                value={searchInput}
                onChange={e => { setSearchInput(e.target.value); setSearchError(''); }}
                style={{ paddingLeft: 32, width: '100%' }}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={searching} style={{ whiteSpace: 'nowrap' }}>
              {searching ? '...' : 'Track'}
            </button>
          </div>
          {searchError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#dc2626' }}>
              <AlertCircle size={13} /> {searchError}
            </div>
          )}
        </form>

        {/* Status filter for company list */}
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: '100%', marginBottom: 10 }}>
          <option value="">Active Shipments</option>
          {ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>

        {/* Company shipment list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loadingShipments ? (
            <div className="loading-spinner">Loading...</div>
          ) : shipments.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 16px' }}>
              <p style={{ fontSize: 13 }}>No active shipments</p>
            </div>
          ) : shipments.map((s: any) => (
            <div
              key={s.id}
              onClick={() => selectFromList(s)}
              style={{
                background: selected?.id === s.id && isOwnShipment ? '#eff6ff' : '#fff',
                border: `1px solid ${selected?.id === s.id && isOwnShipment ? '#3b82f6' : '#e2e8f0'}`,
                borderRadius: 8,
                padding: '12px 14px',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <code style={{ fontSize: 12, fontWeight: 600 }}>{s.trackingNumber}</code>
                <span className={`badge badge-${statusColor(s.status)}`} style={{ fontSize: 10 }}>{s.status.replace(/_/g, ' ')}</span>
              </div>
              <div style={{ fontSize: 13, color: '#475569' }}>{s.receiverName}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{s.receiverAddress}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div>
        {!selected ? (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', color: '#94a3b8' }}>
            <MapPin size={40} style={{ marginBottom: 12, color: '#cbd5e1' }} />
            <p style={{ fontSize: 14 }}>Select a shipment or enter a tracking number</p>
          </div>
        ) : (
          <>
            {/* Shipment header */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <code style={{ fontSize: 14, fontWeight: 700 }}>{selected.trackingNumber}</code>
                    <span className={`badge badge-${statusColor(selected.status)}`}>{selected.status.replace(/_/g, ' ')}</span>
                    {!isOwnShipment && (
                      <span style={{ fontSize: 11, background: '#fef9c3', color: '#854d0e', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                        Read-only
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#475569' }}>
                    <strong>To:</strong> {selected.receiverName}
                    {selected.receiverAddress ? ` — ${selected.receiverAddress}` : ''}
                    {selected.receiverCity ? `, ${selected.receiverCity}` : ''}
                  </div>
                  {selected.transporter && (
                    <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                      <strong>Transporter:</strong> {selected.transporter.firstName} {selected.transporter.lastName}
                    </div>
                  )}
                  {selected.departureTime && (
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                      Departed: {new Date(selected.departureTime).toLocaleString()}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {canAddUpdate && (
                    <button className="btn btn-primary" style={{ fontSize: 13 }}
                      onClick={() => { setShowAddUpdate(p => !p); setUpdateError(''); }}>
                      <Plus size={14} /> Add Update
                    </button>
                  )}
                  {isOwnShipment && (
                    <button className="btn btn-ghost" style={{ fontSize: 13 }}
                      onClick={() => navigate(`/shipments/${selected.id}`)}>
                      <ExternalLink size={14} /> View Detail
                    </button>
                  )}
                </div>
              </div>

              {/* Add update form */}
              {showAddUpdate && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                  {updateError && (
                    <div style={{ background: '#fee2e2', color: '#991b1b', padding: '6px 10px', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>
                      {updateError}
                    </div>
                  )}
                  <form onSubmit={handleAddUpdate}>
                    <div className="form-grid">
                      {['SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'COMPANY_AGENT'].includes(user?.role ?? '') && shipmentIsMoving && (
                        <div className="form-group">
                          <label>Status</label>
                          <select value={updateForm.status} onChange={e => setUpdateForm(p => ({ ...p, status: e.target.value }))}>
                            <option value="">— Keep current —</option>
                            <option value="IN_TRANSIT">In Transit</option>
                            <option value="RETURNED">Returned</option>
                            <option value="CANCELLED">Cancelled</option>
                          </select>
                        </div>
                      )}
                      <div className="form-group">
                        <label>Location</label>
                        <input value={updateForm.location} onChange={e => setUpdateForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Kigali Hub" />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <input value={updateForm.description} onChange={e => setUpdateForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Package arrived at sorting facility" />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button type="submit" className="btn btn-primary" disabled={updateLoading} style={{ fontSize: 13 }}>
                        {updateLoading ? 'Saving...' : 'Save Update'}
                      </button>
                      <button type="button" className="btn btn-ghost" style={{ fontSize: 13 }} onClick={() => setShowAddUpdate(false)}>
                        <X size={13} /> Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* Journey panel */}
            <JourneyPanel
              shipment={selected}
              latestLocation={trackingHistory.find((t: any) => t.location)?.location ?? ''}
            />

            {/* Tracking timeline */}
            <div className="card">
              <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: 15, color: '#64748b' }}>Tracking History</h3>
              {loadingHistory ? (
                <div className="loading-spinner">Loading history...</div>
              ) : trackingHistory.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
                  No tracking updates yet.
                </div>
              ) : (
                <div style={{ position: 'relative', paddingLeft: 24 }}>
                  <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, background: '#e2e8f0' }} />
                  {trackingHistory.map((t: any, i: number) => (
                    <div key={t.id ?? i} style={{ position: 'relative', marginBottom: i < trackingHistory.length - 1 ? 20 : 0 }}>
                      <MapPin size={16} style={{ position: 'absolute', left: -24, top: 2, color: i === 0 ? '#3b82f6' : '#94a3b8' }} />
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                        <span className={`badge badge-${statusColor(t.status)}`} style={{ marginRight: 8 }}>
                          {t.status.replace(/_/g, ' ')}
                        </span>
                        {t.location && <span style={{ color: '#64748b', fontWeight: 400 }}>{t.location}</span>}
                      </div>
                      {t.description && <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>{t.description}</div>}
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                        {new Date(t.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
